import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TreeStructure, Camera } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const nextPath = nextParam && nextParam.startsWith("/") ? nextParam : "/dashboard";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [photoPath, setPhotoPath] = useState(null);
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // We need to be authenticated to upload — so do this after register? No, we want preview before.
    // We'll let users register first then upload. For MVP we'll temporarily not upload during register.
    toast.info("Tip: complete sign-up, then you can update your profile photo.");
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ ...form, profile_photo_path: photoPath });
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      // Pass the whole error so the helper can surface validation messages,
      // network failures, and status-specific fallbacks rather than masking
      // them with a generic "Sign-up failed".
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory grain-bg flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-10" data-testid="register-logo-link">
          <span className="w-10 h-10 rounded-full bg-forest text-gold flex items-center justify-center">
            <TreeStructure size={22} weight="duotone" />
          </span>
          <span className="font-display text-2xl text-forest tracking-tight">LegacyLink</span>
        </Link>
        <div className="legacy-card p-8 md:p-10">
          <h1 className="font-display text-3xl text-forest mb-1">Create your account</h1>
          <p className="text-sageGray text-sm mb-8">Join your family — or start a new circle.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-forest">Full name</Label>
              <Input value={form.name} onChange={onChange("name")} required className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-forest">Email</Label>
              <Input type="email" value={form.email} onChange={onChange("email")} required className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-forest">Phone</Label>
              <Input value={form.phone} onChange={onChange("phone")} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="register-phone-input" />
            </div>
            <div>
              <Label className="text-forest">Password</Label>
              <Input type="password" value={form.password} onChange={onChange("password")} required minLength={6} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="register-password-input" />
            </div>
            <Button disabled={busy} type="submit" className="w-full bg-forest hover:bg-forestSoft text-ivory h-11 rounded-full mt-2" data-testid="register-submit-button">
              {busy ? "Creating account…" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-sageGray text-center mt-6">
            Already a member? <Link to={`/login${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`} className="text-forest underline decoration-gold underline-offset-4" data-testid="register-to-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
